import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import RowsIndicator from '../common/RowsIndicator';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { FiFilter, FiPlus, FiDownload, FiColumns } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import { AddContactModal } from './AddContactModal';
import { formatPhoneNumber } from './phoneUtils';

// Import shared components
import { ColumnFilterDropdown, useFilterPopupPortal } from '../common/filters';
import { ColumnVisibilityDropdown, TooltipCell, ResetButton, SortIcon, sortIconDirection } from '../common';
import { loadXLSX } from '../../utils/xlsxLoader';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useUserPreference } from '../../hooks/useUserPreference';
import { USER_PREFERENCE_KEYS } from '../../constants/userPreferences';

const DEFAULT_COLUMN_ORDER = [
  'Name',
  'Account.Name',
  'Email',
  'MobilePhone',
  'GMB_Type__c',
  'GMB_Region__c',
  'Project_Site_Contact_Last_Modified_By__c',
  'Project_Site_Contact_Last_Modified_Date__c',
  'Email_Alternate__c',
  'Personal_Email__c',
  'Title',
  'Department',
  'Phone',
  'Fax',
  'MailingAddress',
  'OtherAddress',
  'GMB_Account_Billing_State_Short_Code__c',
];

const DEFAULT_VISIBLE_COLUMNS = [
  'Name',
  'Account.Name',
  'Email',
  'MobilePhone',
  'GMB_Type__c',
  'GMB_Region__c',
  'Project_Site_Contact_Last_Modified_By__c',
  'Project_Site_Contact_Last_Modified_Date__c',
];

function mergeColumnOrder(savedOrder) {
  if (!Array.isArray(savedOrder)) {
    return DEFAULT_COLUMN_ORDER;
  }

  const merged = [...savedOrder];
  DEFAULT_COLUMN_ORDER.forEach((key) => {
    if (!merged.includes(key)) {
      merged.push(key);
    }
  });
  return merged.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

function mergeVisibleColumns(savedColumns) {
  if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }
  return savedColumns.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

const DEFAULT_PINNED_COLUMNS = [];

function mergePinnedColumns(savedPins) {
  if (!Array.isArray(savedPins)) {
    return DEFAULT_PINNED_COLUMNS;
  }
  return savedPins.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

function formatContactAddress(contact, prefix) {
  const parts = [
    contact[`${prefix}Street`],
    contact[`${prefix}City`],
    contact[`${prefix}State`],
    contact[`${prefix}PostalCode`],
    contact[`${prefix}Country`],
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : '-';
}

const ITEMS_PER_PAGE = 50;
const INITIAL_LOAD_COUNT = 50;

// TooltipCell and ColumnFilterDropdown are now imported from '../common'

export function ManageContacts() {
  const { authFetch, isReady } = useAuthFetch();
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentDisplayCount, setCurrentDisplayCount] = useState(INITIAL_LOAD_COUNT);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'Name', direction: 'asc' });
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [columnFilterValues, setColumnFilterValues] = useState({
    type: [],
    region: [],
    lastModifiedBy: [],
    lastModifiedDate: []
  });
  const [columnFilters, setColumnFilters] = useState({
    'Name': '',
    'Account.Name': '',
    'Email': '',
    'MobilePhone': ''
  });
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const observerRef = useRef(null);
  const loadingRef = useRef(null);
  const tableScrollRef = useRef(null);
  const hScrollbarRef = useRef(null);
  const {
    filterDropdownRef,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  } = useFilterPopupPortal({
    activeFilterColumn,
    setActiveFilterColumn,
    tableScrollRef,
    hScrollbarRef,
  });
  const hScrollLastRef = useRef({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const columnSelectorRef = useRef(null);
  const columnButtonRef = useRef(null);
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [columnSelectorPosition, setColumnSelectorPosition] = useState({ top: 0, left: 0 });

  const {
    value: visibleColumns,
    setValue: setVisibleColumns,
    save: saveVisibleColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.CONTACTS_VISIBLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    {
      mergeWithDefault: mergeVisibleColumns,
      localStorageKey: 'contacts_visibleColumns',
    }
  );

  const {
    value: columnOrder,
    setValue: setColumnOrder,
    save: saveColumnOrder,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.CONTACTS_COLUMN_ORDER,
    DEFAULT_COLUMN_ORDER,
    {
      mergeWithDefault: mergeColumnOrder,
      localStorageKey: 'contacts_columnOrder',
    }
  );

  const {
    value: pinnedColumns,
    setValue: setPinnedColumns,
    save: savePinnedColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.CONTACTS_PINNED_COLUMNS,
    DEFAULT_PINNED_COLUMNS,
    {
      mergeWithDefault: mergePinnedColumns,
      localStorageKey: 'contacts_pinnedColumns',
    }
  );

  useEffect(() => {
    fetchContacts();
  }, [isReady]);

  const fetchContacts = async () => {
    if (!isReady) return;
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/all-contacts`);
      const data = await response.json();
      const records = Array.isArray(data?.records) ? data.records : [];
      setAllContacts(records);
      setCurrentDisplayCount(INITIAL_LOAD_COUNT);
      setHasMoreData(records.length > INITIAL_LOAD_COUNT);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setAllContacts([]);
      setLoading(false);
    }
  };

  // Intersection Observer for infinite scroll (root = table scroller, same as Projects)
  useEffect(() => {
    if (loading || loadingMore || !hasMoreData) return;

    const root = tableScrollRef.current;
    const sentinel = loadingRef.current;
    if (!root || !sentinel) return;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancelled) return;
        if (entries[0]?.isIntersecting) {
          loadMoreData();
        }
      },
      { root, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [loading, loadingMore, hasMoreData, currentDisplayCount]);

  const loadMoreData = () => {
    if (loadingMore || !hasMoreData) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      setCurrentDisplayCount(prev => {
        const newCount = prev + ITEMS_PER_PAGE;
        const filteredData = getFilteredAndSortedData();
        const hasMore = newCount < filteredData.length;
        setHasMoreData(hasMore);
        setLoadingMore(false);
        return newCount;
      });
    }, 500); // Simulate loading delay
  };

  // Helper to format date to MM/DD/YYYY, h:mm:ss am/pm
  const formatFullTimestamp = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${mm}/${dd}/${yyyy}, ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  // Helper to format last modified date as MM/DD/YYYY (used for dropdown filter values)
  const formatMMDDYYYY = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Get filtered and sorted data
  const getFilteredAndSortedData = () => {
    let filtered = Array.isArray(allContacts) ? allContacts : [];

    // Apply column filters for searchable fields
    filtered = filtered.filter(contact => {
      if (columnFilters['Name'] && 
          !contact.Name?.toLowerCase().includes(columnFilters['Name'].toLowerCase())) {
        return false;
      }
      if (columnFilters['Account.Name'] && 
          !contact.Account?.Name?.toLowerCase().includes(columnFilters['Account.Name'].toLowerCase())) {
        return false;
      }
      if (columnFilters['Email'] && 
          !contact.Email?.toLowerCase().includes(columnFilters['Email'].toLowerCase())) {
        return false;
      }
      if (columnFilters['MobilePhone'] && 
          !contact.MobilePhone?.toLowerCase().includes(columnFilters['MobilePhone'].toLowerCase())) {
        return false;
      }
      return true;
    });

    // Apply dropdown filters
    filtered = filtered.filter(contact => {
      if (columnFilterValues.type.length > 0 && 
          !columnFilterValues.type.includes(contact.GMB_Type__c)) {
        return false;
      }
      if (columnFilterValues.region.length > 0 && 
          !columnFilterValues.region.includes(contact.GMB_Region__c)) {
        return false;
      }
      if (columnFilterValues.lastModifiedBy.length > 0 &&
          !columnFilterValues.lastModifiedBy.includes(contact.Project_Site_Contact_Last_Modified_By__c)) {
        return false;
      }
      if (columnFilterValues.lastModifiedDate.length > 0) {
        const formatted = formatMMDDYYYY(contact.Project_Site_Contact_Last_Modified_Date__c);
        if (!columnFilterValues.lastModifiedDate.includes(formatted)) {
          return false;
        }
      }
      return true;
    });

    // Apply global search
    if (searchTerm) {
      filtered = filtered.filter(contact => {
        const searchStr = searchTerm.toLowerCase();
        return (
          contact.Name?.toLowerCase().includes(searchStr) ||
          contact.Account?.Name?.toLowerCase().includes(searchStr) ||
          contact.Email?.toLowerCase().includes(searchStr) ||
          contact.MobilePhone?.toLowerCase().includes(searchStr) ||
          contact.GMB_Type__c?.toLowerCase().includes(searchStr) ||
          contact.GMB_Region__c?.toLowerCase().includes(searchStr)
        );
      });
    }

    // Apply sorting (supports nested keys like 'Account.Name' and date key)
    const getValueByKey = (obj, key) => {
      if (!obj || !key) return undefined;
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        return obj[parent]?.[child];
      }
      return obj[key];
    };

    filtered.sort((a, b) => {
      const aRaw = getValueByKey(a, sortConfig.key);
      const bRaw = getValueByKey(b, sortConfig.key);

      // Normalize nulls
      const aIsNull = aRaw === undefined || aRaw === null || aRaw === '';
      const bIsNull = bRaw === undefined || bRaw === null || bRaw === '';
      if (aIsNull && bIsNull) return 0;
      if (aIsNull) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bIsNull) return sortConfig.direction === 'asc' ? 1 : -1;

      // Date sort for Last Modified Date
      if (sortConfig.key === 'Project_Site_Contact_Last_Modified_Date__c') {
        const aTime = new Date(aRaw).getTime();
        const bTime = new Date(bRaw).getTime();
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      const aValue = String(aRaw).toLowerCase();
      const bValue = String(bRaw).toLowerCase();
      if (aValue === bValue) return 0;
      const cmp = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    return filtered;
  };

  // Get column display value for export and table cells
  const getColumnDisplayValue = (contact, column) => {
    if (!contact || !column) return '-';

    if (column.key === 'MailingAddress') {
      return formatContactAddress(contact, 'Mailing');
    }

    if (column.key === 'OtherAddress') {
      return formatContactAddress(contact, 'Other');
    }

    if (column.key.includes('.')) {
      const [parent, child] = column.key.split('.');
      const value = contact[parent]?.[child];
      return value !== undefined && value !== null && value !== '' ? value : '-';
    }

    if (column.key === 'Project_Site_Contact_Last_Modified_Date__c') {
      const value = contact[column.key];
      return value ? formatFullTimestamp(value) : '-';
    }

    if (column.key === 'MobilePhone' || column.key === 'Phone' || column.key === 'Fax') {
      const value = contact[column.key];
      return value ? formatPhoneNumber(value) : '-';
    }

    const directValue = contact[column.key];
    if (directValue === undefined || directValue === null || directValue === '') return '-';

    return directValue;
  };

  // Handle Excel export
  const handleExportToExcel = async () => {
    if (isExporting) return;

    if (columns.length === 0) {
      console.warn('No columns selected for export');
      return;
    }

    const exportColumns = columns.filter((column) => visibleColumns.includes(column.key));
    if (exportColumns.length === 0) {
      console.warn('No visible columns selected for export');
      return;
    }

    try {
      setIsExporting(true);
      const XLSX = await loadXLSX();
      if (!XLSX) {
        console.error('XLSX library not available');
        return;
      }

      const headers = exportColumns.map((column) => column.label);
      const exportData = getFilteredAndSortedData();
      const rows = exportData.map((contact) =>
        exportColumns.map((column) => getColumnDisplayValue(contact, column))
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
      XLSX.writeFile(workbook, 'contacts.xlsx');
    } catch (error) {
      console.error('Failed to export contacts:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Update displayed contacts based on current display count
  const displayedContacts = React.useMemo(() => {
    const filteredData = getFilteredAndSortedData();
    const sliced = filteredData.slice(0, currentDisplayCount);
    
    // Update hasMoreData based on current filtered data
    const hasMore = currentDisplayCount < filteredData.length;
    if (hasMoreData !== hasMore) {
      setHasMoreData(hasMore);
    }
    
    return sliced;
  }, [allContacts, searchTerm, columnFilterValues, columnFilters, sortConfig, currentDisplayCount, hasMoreData]);

  // Total filtered count for indicator
  const totalFilteredCount = React.useMemo(() => {
    return getFilteredAndSortedData().length;
  }, [allContacts, searchTerm, columnFilterValues, columnFilters, sortConfig]);

  // Sorting function
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
    // Reset display count when sorting changes
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  // Get unique values for filters
  const getUniqueValues = (data, key) => {
    const items = Array.isArray(data) ? data : [];
    if (key === 'Account.Name') {
      return [...new Set(items.map(item => item.Account?.Name))]
        .filter(Boolean)
        .sort();
    }
    if (key === 'Project_Site_Contact_Last_Modified_Date__c') {
      const format = (ds) => {
        if (!ds) return null;
        const d = new Date(ds);
        if (isNaN(d.getTime())) return null;
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${mm}/${dd}/${yyyy}`;
      };
      return [...new Set(items.map(item => format(item[key])))]
        .filter(Boolean)
        .sort();
    }
    return [...new Set(items.map(item => item[key]))]
      .filter(Boolean)
      .sort();
  };

  // Handle column filter change
  const handleColumnFilterChange = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Toggle column filter
  const toggleColumnFilter = (columnName, event) => {
    toggleFilterPopup(columnName, event);
  };

  // Handle column filter select
  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues(prev => ({
      ...prev,
      [columnName]: prev[columnName].includes(value)
        ? prev[columnName].filter(v => v !== value)
        : [...prev[columnName], value]
    }));
    // Reset display count when filter changes
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  // Column configuration (px widths — required for sticky pin offsets, same as Projects)
  const originalColumns = [
    { key: 'Name', label: 'Name', searchable: true, width: 220 },
    { key: 'Account.Name', label: 'Account Name', searchable: true, width: 240 },
    { key: 'Email', label: 'Email', searchable: true, width: 220 },
    { key: 'MobilePhone', label: 'Mobile Phone', searchable: true, width: 160 },
    { key: 'GMB_Type__c', label: 'Type', filterable: true, filterKey: 'type', width: 150 },
    { key: 'GMB_Region__c', label: 'Region', filterable: true, filterKey: 'region', width: 150 },
    { key: 'Project_Site_Contact_Last_Modified_By__c', label: 'Last Modified By', filterable: true, filterKey: 'lastModifiedBy', width: 180 },
    { key: 'Project_Site_Contact_Last_Modified_Date__c', label: 'Last Modified Date', filterable: true, filterKey: 'lastModifiedDate', width: 180 },
    { key: 'Email_Alternate__c', label: 'Email - Alternate', width: 200 },
    { key: 'Personal_Email__c', label: 'Personal Email', width: 200 },
    { key: 'Title', label: 'Title', width: 140 },
    { key: 'Department', label: 'Department', width: 140 },
    { key: 'Phone', label: 'Phone', width: 140 },
    { key: 'Fax', label: 'Fax', width: 140 },
    { key: 'MailingAddress', label: 'Mailing Address', width: 260 },
    { key: 'OtherAddress', label: 'Alternate Address', width: 260 },
    { key: 'GMB_Account_Billing_State_Short_Code__c', label: 'Account Billing State (Short Code)', width: 180 },
  ];

  const columns = [...originalColumns].sort((a, b) => {
    const aIdx = columnOrder.indexOf(a.key);
    const bIdx = columnOrder.indexOf(b.key);
    return (aIdx !== -1 ? aIdx : 99) - (bIdx !== -1 ? bIdx : 99);
  });

  const pinnedVisibleKeys = pinnedColumns.filter((key) => visibleColumns.includes(key));
  const unpinnedVisibleColumns = columns.filter(
    (column) => visibleColumns.includes(column.key) && !pinnedVisibleKeys.includes(column.key)
  );
  const visibleTableColumns = [
    ...pinnedVisibleKeys
      .map((key) => originalColumns.find((column) => column.key === key))
      .filter(Boolean),
    ...unpinnedVisibleColumns,
  ];
  const tableMinWidth = visibleTableColumns.reduce((sum, column) => sum + column.width, 0) || 1;
  const lastPinnedKey = pinnedVisibleKeys[pinnedVisibleKeys.length - 1] || null;

  const getPinnedLeftOffset = (columnKey) => {
    let left = 0;
    for (const key of pinnedVisibleKeys) {
      if (key === columnKey) return left;
      const col = originalColumns.find((c) => c.key === key);
      left += col?.width || 0;
    }
    return 0;
  };

  const pinnedColumnsWidth = pinnedVisibleKeys.reduce((sum, key) => {
    const col = originalColumns.find((c) => c.key === key);
    return sum + (col?.width || 0);
  }, 0);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const clientWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const overflow = Math.max(0, scrollWidth - clientWidth);
      const last = hScrollLastRef.current;
      if (
        Math.abs(overflow - last.overflow) < 1 &&
        Math.abs(scrollWidth - last.scrollWidth) < 1 &&
        Math.abs(clientWidth - last.clientWidth) < 1
      ) {
        return;
      }
      hScrollLastRef.current = { overflow, scrollWidth, clientWidth };
      setHScroll({ overflow, scrollWidth, clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tableMinWidth, pinnedColumnsWidth, loading, displayedContacts.length]);

  const handleTableScroll = (event) => {
    const bar = hScrollbarRef.current;
    if (!bar) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(bar.scrollLeft - next) >= 1) bar.scrollLeft = next;
  };

  const handleHScrollbarScroll = (event) => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(scroller.scrollLeft - next) >= 1) scroller.scrollLeft = next;
  };

  const handleTableWheelCapture = (event) => {
    if (!pinnedColumnsWidth) return;
    const pinnedEdgeEl = event.currentTarget.querySelector('.projects-col-pinned-last');
    if (!pinnedEdgeEl) return;
    const pinnedRight = pinnedEdgeEl.getBoundingClientRect().right;
    if (event.clientX > pinnedRight) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
    if (!isHorizontal) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const getColumnStyle = (column, { isHeader = false } = {}) => {
    const isPinned = pinnedVisibleKeys.includes(column.key);
    if (isPinned) {
      return {
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        position: 'sticky',
        left: `${getPinnedLeftOffset(column.key)}px`,
        zIndex: isHeader ? 30 : 4,
      };
    }
    return {
      minWidth: `${column.width}px`,
      width: `${(column.width / tableMinWidth) * 100}%`,
    };
  };

  const handleToggleColumn = (columnKey) => {
    const isHiding = visibleColumns.includes(columnKey);
    const newColumns = isHiding
      ? visibleColumns.filter((key) => key !== columnKey)
      : [...visibleColumns, columnKey];
    setVisibleColumns(newColumns);
    saveVisibleColumns(newColumns);

    if (isHiding && pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
    }
  };

  const handleTogglePin = (columnKey) => {
    if (pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
      return;
    }

    if (!visibleColumns.includes(columnKey)) {
      const nextVisible = [...visibleColumns, columnKey];
      setVisibleColumns(nextVisible);
      saveVisibleColumns(nextVisible);
    }

    const nextPins = [...pinnedColumns, columnKey];
    setPinnedColumns(nextPins);
    savePinnedColumns(nextPins);
  };

  const handleReorderColumn = (fromIndex, toIndex, groupColumns) => {
    const fromKey = groupColumns[fromIndex].key;
    const toKey = groupColumns[toIndex].key;

    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const idxA = newOrder.indexOf(fromKey);
      const idxB = newOrder.indexOf(toKey);
      if (idxA !== -1 && idxB !== -1) {
        const [movedItem] = newOrder.splice(idxA, 1);
        newOrder.splice(idxB, 0, movedItem);
      }
      return newOrder;
    });
  };

  const handleReorderColumnEnd = () => {
    saveColumnOrder();
  };

  const handleColumnSelectorClick = () => {
    setIsColumnSelectorOpen((prev) => {
      const next = !prev;
      if (next && columnButtonRef.current) {
        const rect = columnButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 208;
        let left = rect.left;
        let top = rect.bottom + 4;

        if (left + dropdownWidth > window.innerWidth - 8) {
          left = rect.right - dropdownWidth;
        }
        if (left < 8) {
          left = 8;
        }
        setColumnSelectorPosition({ top, left });
      }
      return next;
    });
  };

  const clearStoredData = async () => {
    localStorage.removeItem('contacts_columnOrder');
    localStorage.removeItem('contacts_visibleColumns');
    localStorage.removeItem('contacts_pinnedColumns');

    setSearchTerm('');
    setSortConfig({ key: 'Name', direction: 'asc' });
    setColumnFilters({
      Name: '',
      'Account.Name': '',
      Email: '',
      MobilePhone: '',
    });
    setColumnFilterValues({
      type: [],
      region: [],
      lastModifiedBy: [],
      lastModifiedDate: [],
    });
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);

    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setPinnedColumns(DEFAULT_PINNED_COLUMNS);

    await Promise.all([
      saveColumnOrder(DEFAULT_COLUMN_ORDER),
      saveVisibleColumns(DEFAULT_VISIBLE_COLUMNS),
      savePinnedColumns(DEFAULT_PINNED_COLUMNS),
    ]);

    window.location.reload();
  };

  useEffect(() => {
    if (!isColumnSelectorOpen) return undefined;
    function handleClickOutside(event) {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setIsColumnSelectorOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSelectorOpen]);

  const draggedHeaderKeyRef = useRef(null);
  const draggedHeaderWasPinnedRef = useRef(false);

  const handleHeaderDragStart = (e, columnKey) => {
    if (e.target.closest?.('[data-pin-toggle]')) {
      e.preventDefault();
      return;
    }
    draggedHeaderKeyRef.current = columnKey;
    draggedHeaderWasPinnedRef.current = pinnedColumns.includes(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleHeaderDragOver = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = draggedHeaderKeyRef.current;
    if (!draggedKey || draggedKey === targetKey) return;

    const draggedPinned = pinnedColumns.includes(draggedKey);
    const targetPinned = pinnedColumns.includes(targetKey);

    if (draggedPinned && targetPinned) {
      setPinnedColumns((prev) => {
        const next = [...prev];
        const idxA = next.indexOf(draggedKey);
        const idxB = next.indexOf(targetKey);
        if (idxA === -1 || idxB === -1) return prev;
        const [movedItem] = next.splice(idxA, 1);
        next.splice(idxB, 0, movedItem);
        return next;
      });
      return;
    }

    if (!draggedPinned && !targetPinned) {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const idxA = newOrder.indexOf(draggedKey);
        const idxB = newOrder.indexOf(targetKey);
        if (idxA !== -1 && idxB !== -1) {
          const [movedItem] = newOrder.splice(idxA, 1);
          newOrder.splice(idxB, 0, movedItem);
        }
        return newOrder;
      });
    }
  };

  const handleHeaderDragEnd = () => {
    const wasPinned = draggedHeaderWasPinnedRef.current;
    draggedHeaderKeyRef.current = null;
    draggedHeaderWasPinnedRef.current = false;
    if (wasPinned) {
      savePinnedColumns();
    } else {
      saveColumnOrder();
    }
  };

  // Add contact handler
  const handleContactAdded = (newContacts) => {
    setAllContacts(Array.isArray(newContacts) ? newContacts : []);
  };

  // Handle row click
  const handleRowClick = async (contact) => {
    if (!isReady) return;
    setSelectedRowId(contact.Id);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/contacts/id/${contact.Id}`);
      const data = await response.json();
      
      if (data.records && data.records.length > 0) {
        const contactDetails = data.records[0];
        setSelectedContact(contactDetails);
        setIsAddContactModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
    }
  };

  

  return (
    <div className="projects-page-card">
      <div className="projects-table-toolbar">
        <div className="projects-table-toolbar-row flex items-center gap-4 max-md:gap-2">
          <form className="projects-toolbar-search flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-white border rounded-[31px] text-zinc-500 w-[30%] shadow-sm max-md:flex-1 max-md:w-auto max-md:bg-stone-50 max-md:border-0 max-md:rounded-full max-md:shadow-none">
            <img
              loading="lazy"
              src={Icon13}
              className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
              alt=""
            />
            <input
              type="search"
              id="contactSearch"
              placeholder="Search"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                setHasMoreData(true);
              }}
            />
          </form>

          <button
            onClick={() => setIsAddContactModalOpen(true)}
            disabled={loading}
            className={`ml-2 px-6 py-2 rounded transition-colors max-md:hidden ${
              loading
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-red-700 text-white hover:bg-red-800'
            }`}
          >
            + Add Contact
          </button>
          <button
            onClick={() => setIsAddContactModalOpen(true)}
            disabled={loading}
            aria-label="Add Contact"
            className={`projects-toolbar-add hidden max-md:flex items-center justify-center rounded-full w-10 h-10 ${
              loading ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-red-700 text-white'
            }`}
          >
            <FiPlus className="w-5 h-5" />
          </button>

          <div className="flex-grow max-md:hidden"></div>

          <div className="projects-toolbar-actions">
            <div className="relative projects-toolbar-columns">
              <button
                type="button"
                ref={columnButtonRef}
                onClick={handleColumnSelectorClick}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none"
                title="Show/Hide Columns"
              >
                <FiColumns className="h-4 w-4" />
                <span className="ml-2 max-md:hidden">Columns</span>
              </button>
              <ColumnVisibilityDropdown
                ref={columnSelectorRef}
                isOpen={isColumnSelectorOpen}
                onClose={() => setIsColumnSelectorOpen(false)}
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onReorderColumn={handleReorderColumn}
                onReorderEnd={handleReorderColumnEnd}
                position={columnSelectorPosition}
              />
            </div>

            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={loading || displayedContacts.length === 0 || isExporting}
              className={`projects-toolbar-export flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-2 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none max-md:mr-0 ${
                loading || displayedContacts.length === 0 || isExporting ? 'cursor-not-allowed opacity-60' : ''
              }`}
              title="Download Excel"
            >
              <FiDownload className="h-4 w-4" />
            </button>

            <ResetButton onClick={clearStoredData} tooltip="Reset" />
          </div>
        </div>

        <AddContactModal
          isOpen={isAddContactModalOpen}
          onClose={() => {
            setIsAddContactModalOpen(false);
            setSelectedContact(null);
          }}
          onContactAdded={handleContactAdded}
          contact={selectedContact}
        />
      </div>

      <div
        ref={tableScrollRef}
        className="projects-table-scroll projects-table-scroll--proxy-h"
        onScroll={handleTableScroll}
        onWheelCapture={handleTableWheelCapture}
      >
        <table
          className="projects-data-table"
          style={{
            width: `max(100%, ${tableMinWidth}px)`,
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <colgroup>
            {visibleTableColumns.map((column) => {
              const isPinned = pinnedVisibleKeys.includes(column.key);
              return (
                <col
                  key={column.key}
                  style={
                    isPinned
                      ? {
                          width: `${column.width}px`,
                          minWidth: `${column.width}px`,
                        }
                      : {
                          width: `${(column.width / tableMinWidth) * 100}%`,
                          minWidth: `${column.width}px`,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {visibleTableColumns.map((column) => {
                const isPinned = pinnedVisibleKeys.includes(column.key);
                return (
                  <th
                    key={column.key}
                    style={getColumnStyle(column, { isHeader: true })}
                    className={`group/header px-4 py-3 max-md:whitespace-normal cursor-grab active:cursor-grabbing transition-colors hover:bg-gray-100 ${
                      isPinned ? 'projects-col-pinned' : ''
                    } ${column.key === lastPinnedKey ? 'projects-col-pinned-last' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleHeaderDragStart(e, column.key)}
                    onDragOver={(e) => handleHeaderDragOver(e, column.key)}
                    onDragEnd={handleHeaderDragEnd}
                  >
                    <div className="flex flex-col gap-2">
                      <div
                        className="flex items-center justify-center text-sm font-medium text-gray-500 cursor-pointer whitespace-nowrap"
                        onClick={() => handleSort(column.key)}
                      >
                        <span className="truncate" title={column.label}>{column.label}</span>
                        <span className="ml-1 inline-flex w-4 shrink-0 items-center justify-center text-gray-500">
                          <SortIcon direction={sortIconDirection(sortConfig, column.key)} />
                        </span>
                        <button
                          type="button"
                          data-pin-toggle
                          title={isPinned ? 'Unpin column' : 'Pin column to left'}
                          aria-label={isPinned ? `Unpin ${column.label}` : `Pin ${column.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePin(column.key);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          draggable={false}
                          className={`projects-pin-toggle ml-1 p-0.5 rounded shrink-0 transition-opacity ${
                            isPinned
                              ? 'is-pinned text-red-600 hover:text-red-800 hover:bg-red-50'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                          }`}
                        >
                          <span className="inline-flex">
                            {isPinned ? (
                              <MdPushPin className="h-3.5 w-3.5" />
                            ) : (
                              <MdOutlinePushPin className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </button>
                      </div>
                      <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                        {column.filterable ? (
                          <div
                            className="relative"
                            ref={activeFilterColumn === column.filterKey ? filterDropdownRef : null}
                            style={getFilterWrapperStyle(activeFilterColumn === column.filterKey)}
                          >
                            <button
                              onClick={(e) => toggleColumnFilter(column.filterKey, e)}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${
                                columnFilterValues[column.filterKey]?.length > 0 ? 'text-red-600 border-red-500' : 'text-zinc-500'
                              }`}
                            >
                              <span>
                                {columnFilterValues[column.filterKey]?.length > 0
                                  ? `${columnFilterValues[column.filterKey].length} selected`
                                  : 'Filter'}
                              </span>
                              <FiFilter className="ml-2" />
                            </button>
                            {activeFilterColumn === column.filterKey &&
                              renderFilterPopup(
                                <ColumnFilterDropdown
                                  isOpen={activeFilterColumn === column.filterKey}
                                  options={getUniqueValues(allContacts, column.key)}
                                  selectedValues={columnFilterValues[column.filterKey] || []}
                                  onSelect={(value) => handleColumnFilterSelect(column.filterKey, value)}
                                  onClose={closeFilterPopup}
                                  maxHeight={
                                    column.filterKey === 'type' || column.filterKey === 'region'
                                      ? '40vh'
                                      : undefined
                                  }
                                  minWidth={
                                    column.filterKey === 'type' || column.filterKey === 'region'
                                      ? '14rem'
                                      : undefined
                                  }
                                  maxWidth={
                                    column.filterKey === 'type' || column.filterKey === 'region'
                                      ? '18rem'
                                      : undefined
                                  }
                                />
                              )}
                          </div>
                        ) : column.searchable ? (
                          <input
                            type="text"
                            placeholder="Search"
                            className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light text-zinc-500"
                            value={columnFilters[column.key] || ''}
                            onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                          />
                        ) : null}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700" />
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : displayedContacts.length === 0 ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="text-center py-4">No contacts found</td>
              </tr>
            ) : (
              displayedContacts.map((contact) => (
                <tr
                  key={contact.Id}
                  className={`hover:bg-gray-50 cursor-pointer ${
                    contact.Id === selectedRowId ? 'bg-red-50' : ''
                  }`}
                  onClick={() => handleRowClick(contact)}
                >
                  {visibleTableColumns.map((column) => {
                    const isPinned = pinnedVisibleKeys.includes(column.key);
                    return (
                      <TooltipCell
                        key={column.key}
                        content={getColumnDisplayValue(contact, column)}
                        className={`text-gray-900 max-md:whitespace-normal break-words${
                          isPinned ? ' projects-col-pinned' : ''
                        }${column.key === lastPinnedKey ? ' projects-col-pinned-last' : ''}`}
                        align="left"
                        style={getColumnStyle(column)}
                      />
                    );
                  })}
                </tr>
              ))
            )}
            {!loading && displayedContacts.length > 0 && hasMoreData && (
              <tr ref={loadingRef}>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700" />
                        <span className="ml-2">Loading more...</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Scroll down to load more</span>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {!loading && displayedContacts.length > 0 && !hasMoreData && (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    <span className="text-gray-500">No more data to load</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hScroll.overflow > 0 && (
        <div className="projects-h-scrollbar-rail">
          <div
            ref={hScrollbarRef}
            onScroll={handleHScrollbarScroll}
            className="projects-h-scrollbar"
            style={{
              marginLeft: `${pinnedColumnsWidth}px`,
              width: `${Math.max(hScroll.clientWidth - pinnedColumnsWidth, 0)}px`,
            }}
          >
            <div style={{ height: '1px', width: `${Math.max(hScroll.scrollWidth - pinnedColumnsWidth, 0)}px` }} />
          </div>
        </div>
      )}
      <RowsIndicator loadedCount={displayedContacts.length} totalCount={totalFilteredCount} />
    </div>
  );
}
